// --- React ---
import { useState } from "react";
import { useNavigate } from "react-router-dom";

// --- Components ---
import ModalConfirm from "../../../components/modal-comfirm";
import { useLookupParameter } from "../../../utils/get-lookup";
import { Plus } from "lucide-react";
import StationParameterWarningsModal from "../../../components/station/parameter-warnings";
import { notify } from "../../../common/notificationService";
import { Pencil, Trash } from "lucide-react";

// --- Models ---
import { StationParameterWarningItem } from "../../../models/types/stationParameterWarnings";

// --- Services ---
import stationParameterWarningsSvc from "../../../services/api/stationParameterWarnings";
import { useTranslation } from "react-i18next";

interface FormProps {
    stationId: number | string;
    data: StationParameterWarningItem[];
    onSuccess?: () => Promise<void>;
}

export default function StationParameterWarnings({ stationId, data, onSuccess }: FormProps) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { lookupParameter } = useLookupParameter();

    const [loading, setLoading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [itemSelected, setItemSelected] = useState<any | null>(null);
    const [comfirmDelete, setComfirmDelete] = useState<boolean>(false);

    const availableOptions = lookupParameter.filter((item) => !data?.map(p => p.parameterId).includes(item.value));
    const handleRemoveParameter = (item: StationParameterWarningItem) => {
        handleDeleteParameter(item.id);
    };

    const handleDeleteParameter = async (id: number) => {
        setLoading(true);
        try {
            const res = await stationParameterWarningsSvc.deleteStationParameterWarning(id);
            if (res && res.content.status) {
                onSuccess?.();
            }
        } catch (e: any) {
            notify(t("common.error"), t("common.errorMessage"), "danger");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (item: any | null) => {
        setItemSelected(item);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setItemSelected(null);
    };

    const handleConfirmDelete = (item: StationParameterWarningItem | null) => {
        setComfirmDelete(true);
        setItemSelected(item);
    };

    return (
        <>
            {comfirmDelete && itemSelected && (
                <ModalConfirm
                    isOpen={comfirmDelete}
                    onClose={() => setComfirmDelete(false)}
                    title={t("station.modal.confirmDeleteParameter.title")}
                    content={t("station.modal.confirmDeleteParameter.content")}
                    titleComfirm={t("station.modal.confirmDeleteParameter.confirm")}
                    action={() => handleRemoveParameter(itemSelected)}
                />
            )}
            {openModal && (
                <StationParameterWarningsModal
                    isOpen={openModal}
                    onClose={handleCloseModal}
                    data={itemSelected}
                    availableOptions={availableOptions}
                    stationId={stationId}
                    onSuccess={onSuccess}
                />
            )}
            <div className="w-full h-full">
                <div>
                    <div className="">
                        <div className="relative overflow-x-auto rounded-lg border border-gray-200 shadow-sm bg-white">
                            <table className="min-w-full text-sm text-left text-gray-700">
                                <thead className="bg-gray-200 text-sm font-semibold uppercase text-gray-600">
                                    <tr>
                                        <th className="px-4 py-3 text-left w-[50px]">{t("station.table.stt")}</th>
                                        <th className="px-4 py-3 text-left">{t("station.table.code")}</th>
                                        <th className="px-4 py-3 text-left min-w-[240px]">{t("station.table.parameter")}</th>
                                        <th className="px-4 py-3 text-left">{t("station.table.unit")}</th>
                                        <th className="px-4 py-3 text-left min-w-[120px]">{t("station.table.minValue")}</th>
                                        <th className="px-4 py-3 text-left min-w-[120px]">{t("station.table.maxValue")}</th>
                                        <th className="px-4 py-3 text-left w-[100px]"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.length > 0 &&
                                        data.map((parameter, index) => {
                                            const unit = lookupParameter.find(p => p.value === parameter.parameterId)?.unitName;
                                            const code = lookupParameter.find(p => p.value === parameter.parameterId)?.code;
                                            const name = lookupParameter.find(p => p.value === parameter.parameterId)?.name;
                                            return (
                                                <tr key={index} className={`hover:bg-gray-100 ${index % 2 === 1 ? "bg-gray-50" : ""}`}>
                                                    <td className="px-4 py-2 text-left">{index + 1}</td>
                                                    <td className="px-4 py-2 text-left">
                                                        {code || "-"}
                                                    </td>
                                                    <td className="px-4 py-2 text-left">
                                                        {name || "-"}
                                                    </td>
                                                    <td className="px-4 py-2 text-left">
                                                        {unit || "-"}
                                                    </td>
                                                    <td className="px-4 py-2 text-left">
                                                        {parameter?.earlyWarningMin ?? 0}
                                                    </td>
                                                    <td className="px-4 py-2 text-left">
                                                        {parameter?.earlyWarningMax ?? 0}
                                                    </td>
                                                    <td className="flex gap-2 px-4 py-2 text-left">
                                                        <button
                                                            title="Xóa"
                                                            type="button"
                                                            className="btn bg-red-500 text-white"
                                                            onClick={() => handleConfirmDelete(parameter)}
                                                        >
                                                            <Trash size={16} />
                                                        </button>
                                                        <button
                                                            title="Sửa"
                                                            type="button"
                                                            className="btn bg-blue-500 text-white"
                                                            onClick={() => handleOpenModal({
                                                                ...parameter,
                                                                unit: unit,
                                                                name: name,
                                                                code: code,
                                                            })}
                                                        >
                                                            <Pencil size={16} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    <tr>
                                        <td colSpan={7} className="px-4 py-2 text-center text-gray-500 hover:bg-gray-100 border-t border-gray-200">
                                            <div onClick={() => handleOpenModal(null)} className="w-full flex justify-center items-center gap-2 cursor-pointer">
                                                <Plus className="inline" size={20} /> {t("station.actions.addParameter")}
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}